import { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router'

import { DailyStatusBody } from '../../components/daily/DailyStatusView'
import { PairCard } from '../../components/daily/PairCard'
import { SelfReportForm } from '../../components/journal/SelfReportForm'
import { PageHeader } from '../../components/PageHeader'
import { Card, EmptyState, Notice, ProblemNotice, RecordingStatusPill } from '../../components/ui'
import { getDailyStatus, getRecording, type RecordingRecord } from '../../lib/api'
import { useRetryLanding, type ArmLanding } from '../../lib/useRetryLanding'
import { hasDailyStatus, readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { deviceLabel } from '../../lib/devices'
import { PAIRED_SESSION_ENABLED } from '../../lib/features'
import { formatDateTime, formatSessionDate } from '../../lib/format'
import { PAIR_LEAD, pairLinkOf } from '../../lib/pair'
import { isNotFound } from '../../lib/problem'
import { CONDITION_WORDS, RECORDING_OUTCOME } from '../../lib/recordings'
import { ERROR_CODE_COPY } from '../../lib/warnings'
import { useResource } from '../../lib/useResource'
import { usePublishAssistantPage } from '../../contexts/AssistantContext'

/**
 * One past recording — §4.2, "the Today layout for a past date, plus its
 * self-report".
 *
 * Almost none of that is written here. `DailyStatusBody` already renders the
 * whole readout and its own docstring says it exists for "Today and a past
 * date's detail view"; `SelfReportForm` is host-agnostic and takes an id. This
 * file chooses which recording, decides whether a result may be asked for, and
 * says the honest thing when there is none.
 *
 * ── THE ORDER OF THE TWO READS IS LOAD-BEARING ──────────────────────────────
 *
 * `getDailyStatus` answers 404 for an `empty` recording and for a `failed` one,
 * by contract — "callers must not ask for those; the status on the
 * `RecordingRecord` is what says whether to ask at all" (`api.ts:393-399`).
 * Firing both reads in parallel would ask anyway and manufacture exactly the
 * failure criterion 8 forbids rendering as one. One extra round trip buys that.
 */
export function RecordingDetail() {
  const params = useParams<{ recordingId: string }>()
  const recordingId = params.recordingId ?? ''

  const loadRecording = useCallback(
    (signal: AbortSignal) => getRecording(recordingId, { signal }),
    [recordingId],
  )
  const recording = useResource(loadRecording)

  /**
   * The server's post-save record wins over the initial read.
   *
   * `useResource` has no setter, and reloading would spend a round trip to learn
   * what `onSaved` has already handed us. Reset at render time on a changed
   * `recordingId` — the idiom `SelfReportForm` itself uses, and for the same
   * reason: an effect runs a frame later, and a frame is long enough to show one
   * recording's journal under another's heading.
   */
  const [savedRecord, setSavedRecord] = useState<RecordingRecord | null>(null)
  const [seed, setSeed] = useState(recordingId)
  if (seed !== recordingId) {
    setSeed(recordingId)
    setSavedRecord(null)
  }
  const row = savedRecord ?? recording.data
  // agent-1-web: the assistant's chip names this recording's day and condition
  // (the id is the route's own). A no-op without the assistant.
  usePublishAssistantPage(
    row === null ? null : { recordingId: row.recording_id, condition: row.condition, dateLabel: formatSessionDate(row.session_date) },
  )

  /**
   * ── THE DEPENDENCY IS WHAT THE REQUEST DEPENDS ON, NOT THE WHOLE RECORD ──
   *
   * `[row]` is the obvious dependency and it is wrong, because `row` gets a new
   * identity every time a journal is saved: `onSaved` hands the PATCH response
   * into `savedRecord`, which this reads through. A new `loadStatus` identity
   * trips `useResource`'s changed-`load` reset — `setData(null)`,
   * `setIsLoading(true)` — so filing a journal unmounted every score card,
   * showed "Loading this result…" again, and re-issued a request for a payload
   * that had not changed. If that second read failed, a readout the person was
   * reading was replaced by an error, caused entirely by an action the form
   * itself describes as one that "never changes your result".
   *
   * It also defeated the point of `savedRecord`, which exists a few lines above
   * precisely so a save costs no round trip.
   *
   * The request is a function of the recording's id and its status and of
   * nothing else, so those are the dependencies. `undefined -> session_...`
   * still fires the real read when the record first arrives, which is the
   * ordering the header comment is about.
   */
  const recordingStatus = row?.status
  const loadStatus = useCallback(
    (signal: AbortSignal): Promise<DailyStatusResult | null> =>
      recordingStatus === undefined || !hasDailyStatus(recordingStatus)
        ? Promise.resolve(null)
        : getDailyStatus(recordingId, { signal }).then(readDailyStatus),
    [recordingId, recordingStatus],
  )
  const status = useResource(loadStatus)
  /**
   * One landing for both retries here. `recording.error` early-returns a whole
   * different tree, so its "Try again" unmounts with everything around it;
   * `status.error`'s notice unmounts in place. Both end on `<body>`, and the
   * header below is what says which recording is now on screen.
   */
  const [landOn, armLanding] = useRetryLanding(recording.error != null || status.error != null)

  if (recording.isLoading) {
    return (
      <div className="stack stack--wide">
        <UnknownHeader />
        <Card>
          <p className="loading-row" role="status">
            <span className="spinner" aria-hidden="true" /> Loading this recording…
          </p>
        </Card>
      </div>
    )
  }

  /**
   * A MISS AND A BREAKAGE ARE DIFFERENT SENTENCES.
   *
   * The first version of this guard was `isNotFound(error) || row === null`,
   * which reads as one condition and is two: a 500 satisfies the second clause,
   * because `useResource` holds no data after a failure. So any server error at
   * all rendered "that recording is not in your history" — telling a person
   * their recording is gone when the server merely fell over, and offering them
   * no retry for something a retry would have fixed.
   */
  if (recording.error != null) {
    return (
      <div className="stack stack--wide">
        <UnknownHeader />
        {isNotFound(recording.error)
          ? <NotYours />
          : <ReadFailed error={recording.error} onRetry={recording.reload} landing={armLanding} />}
      </div>
    )
  }

  /**
   * Settled, no error, and nothing came back.
   *
   * **Unreachable, and kept anyway.** `useResource<RecordingRecord>` types `data`
   * as nullable and the client throws rather than resolving null, so the two
   * guards above cover every path a request can actually take — a mutation that
   * deletes this branch kills no test, and that is recorded rather than papered
   * over with a test that fakes the state. It stays because `row` has to be
   * narrowed before the render below reads it, and because a blank screen is
   * the worst of the available failures if that ever stops being true.
   */
  if (row === null) {
    return (
      <div className="stack stack--wide">
        <UnknownHeader />
        <NotYours />
      </div>
    )
  }

  const outcome = RECORDING_OUTCOME[row.status]
  // The paired session (Sprint 1.9.13 W4): both blocks are recordings with a
  // page of their own, and each shows the pair card — the eyes-open block's
  // links to the day's reading, the eyes-closed block's to its partner.
  const pair = PAIRED_SESSION_ENABLED ? pairLinkOf(row) : null

  return (
    <div className="stack stack--wide">
      {/*
        THE BACK LINK IS INSIDE THE LANDING TARGET, AND THE REF STAYS ON THE ROOT.

        `← History` used to be a sibling paragraph above the header. It is now
        the header's first child (R25's one page-header pattern, and the shape
        `UserDetail.tsx:139-140` already ships), which means the element a retry
        lands on begins with the link rather than with the date.

        The ref, `tabIndex` and `retry-landing` stay on the `<header>` ROOT and
        never move onto the `<h1>` or the link. `.page.retry-landing` needs both
        classes on one element for its `scroll-margin-block-start`, and measuring
        that margin from the root's top is what keeps the link and the eyebrow
        clear of the sticky top bar below 1024px (at 1024 and above there is no
        top bar and the margin is inert). A `tabindex="-1"` container takes focus
        whatever it contains, so `useRetryLanding` needs no change.
      */}
      <PageHeader
        className="retry-landing"
        ref={landOn}
        tabIndex={-1}
        back={
          <Link className="page__back" to="/history">
            ← History
          </Link>
        }
        // The resolved headband beside the condition, for every outcome --
        // a failed or empty recording has no Daily Status body to carry it
        // (Sprint 1.9.2 S4). An id this build cannot name adds nothing.
        eyebrow={[
          `Recording · ${CONDITION_WORDS[row.condition]}`,
          pair === null ? null : `block ${pair.pairIndex} of 2`,
          deviceLabel(row.device_id),
        ]
          .filter((part): part is string => part !== null)
          .join(' · ')}
        title={formatSessionDate(row.session_date)}
        aside={<RecordingStatusPill status={row.status} />}
      >
        <p className="history__detail">
          Uploaded {formatDateTime(row.created_at)} ·{' '}
          <span className="product-id">{row.recording_id}</span>
        </p>
        {pair !== null && <p className="history__detail">{PAIR_LEAD[pair.pairRole]}</p>}
      </PageHeader>

      {outcome === undefined && (
        <Notice tone="warn">
          <p>
            This app does not recognise the state{' '}
            <code className="mono-cell">{row.status}</code>.
          </p>
        </Notice>
      )}

      <Outcome row={row} status={status} landing={armLanding} />

      {pair !== null && <PairCard recordingId={row.recording_id} pairRole={pair.pairRole} />}

      {/*
        §3.3's "editable afterwards from History", which Stage 5 left for this
        screen. Mounted for ALL FOUR terminal states, not only the scored ones:
        the backend applies no status gate, and an `empty` recording never
        reaches Record's copy of this form at all — its job FAILED, so there is
        no `result_recording_id` to route to — which makes this the only place
        that day can ever be journalled.

        `loaded: true` is a proof obligation rather than a flag: it is reachable
        only below the guard above, so a recording this screen could not read can
        never open a blank form over a journal that exists.
      */}
      <SelfReportForm
        recordingId={row.recording_id}
        original={{
          loaded: true,
          report: row.self_report ?? null,
          provenance: row.self_report_provenance ?? null,
        }}
        onSaved={setSavedRecord}
      />
    </div>
  )
}

/**
 * What a recording's outcome is worth showing.
 *
 * The `scored` and `rejected` branches both render the body: a rejected
 * recording HAS a payload, and `ValidityNote` inside it is where criterion 8's
 * "with their reason" is actually paid. Suppressing it would leave the reason
 * nowhere.
 */
function Outcome({
  row,
  status,
  landing,
}: {
  row: RecordingRecord
  status: { data: DailyStatusResult | null; error: unknown; isLoading: boolean; reload: () => void }
  landing?: ArmLanding
}) {
  const outcome = RECORDING_OUTCOME[row.status]

  if (outcome?.hasResult !== true) {
    // No daily-status request was made for these, and none should be. `empty` is
    // not an error and gets no error styling; `failed` is, and still gets no
    // invented reason — §2.6 puts that detail on the Job, not on this record.
    if (outcome === undefined) return null
    return row.status === 'failed' ? (
      <Notice tone="error">
        <p>{outcome.detail}</p>
        {/* The sentence that used to stand here said the reason "is not stored
            on this recording, so it cannot be shown". That was true and is not
            any more (ask B7): `error_code` is stamped where the terminal row is
            written. It is kept, verbatim, for a recording that predates the
            field -- for those it is still the honest answer. */}
        {row.error_code == null ? (
          <p>
            The reason was reported by the job that ran it and is not stored on this
            recording, so it cannot be shown here.
          </p>
        ) : (
          <p>
            {ERROR_CODE_COPY[row.error_code] ?? (
              <code className="mono-cell">{row.error_code}</code>
            )}
          </p>
        )}
      </Notice>
    ) : (
      // No heading: the pill beside the date already carries `outcome.label`,
      // and repeating it here reads as two different facts about the recording
      // rather than one stated twice.
      <Card>
        <p className="product-empty__body">{outcome.detail}</p>
      </Card>
    )
  }

  return (
    <>
      {row.status === 'rejected' && outcome.detail !== null && (
        <Notice tone="warn">{outcome.detail}</Notice>
      )}
      <DailyResult row={row} status={status} landing={landing} />
    </>
  )
}

function DailyResult({
  row,
  status,
  landing,
}: {
  row: RecordingRecord
  status: { data: DailyStatusResult | null; error: unknown; isLoading: boolean; reload: () => void }
  landing?: ArmLanding
}) {
  if (status.isLoading) {
    return (
      <Card>
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Loading this result…
        </p>
      </Card>
    )
  }
  if (status.error != null) {
    return (
      <ProblemNotice error={status.error} onRetry={status.reload} landing={landing}>
        This result could not be loaded.
      </ProblemNotice>
    )
  }
  if (status.data === null) return null
  // §1.7 — an unusable payload gets a clear unavailable state rather than a
  // half-rendered readout, which is the thing that reads as a measurement.
  if (!status.data.readable) return <Notice tone="warn">{status.data.detail}</Notice>
  /*
    The body is shared with Today, and on Today it knows which recording it is
    showing because Today picked it. Here it has to be told: two property reads,
    no arithmetic and no derived text, so the recording card carries the same
    condition pill and device meta on this route as on that one.

    NO `afterRows` (O5). The Journal-summary card is Today's alone; on this
    screen the journal FORM stays where it is, after `<Outcome>`.
  */
  return (
    <DailyStatusBody
      status={status.data}
      recording={{ condition: row.condition, deviceId: row.device_id }}
    />
  )
}

/**
 * ONE STATE FOR "ABSENT" AND FOR "SOMEONE ELSE'S", because the backend spent
 * code to make them indistinguishable.
 *
 * `application/recordings.py:64-72` builds a cross-user miss as
 * `RecordingNotFoundError("*", recording_id)` — never the actor's id — "so the
 * Problem body is byte-identical to a genuine 404 and the two cases stay
 * indistinguishable". A screen that said "you do not have access to that" would
 * re-open exactly what that is hiding: `recording_id` is a timestamp, so a
 * distinguishable refusal is an existence oracle.
 */
function NotYours() {
  return (
    <Card>
      {/* `EmptyState` renders its title as an `<h2>` by default, which is the
          level `e2e/history.spec.ts:290` and this file's own outcome tests
          read. Copy byte-for-byte as it shipped -- only the layout moved. */}
      <EmptyState
        title="That recording is not in your history"
        action={
          <Link className="btn btn--primary" to="/history">
            Back to History
          </Link>
        }
      >
        <p>It may have been deleted, or the link may be wrong.</p>
      </EmptyState>
    </Card>
  )
}

function ReadFailed(
  { error, onRetry, landing }: { error: unknown; onRetry: () => void; landing?: ArmLanding },
) {
  return (
    <ProblemNotice error={error} onRetry={onRetry} landing={landing}>
      This recording could not be loaded.
    </ProblemNotice>
  )
}

/**
 * The three states that do not know the recording's date still need a heading.
 *
 * Every other page in this app opens with an `<h1>`, and the loading, not-found
 * and read-failed branches had none — so the first heading a screen reader met
 * was the `<h2>` inside the card, which reads as a section of a page that is not
 * there. The date is genuinely unknown in all three, so the heading says the
 * only true thing available.
 */
function UnknownHeader() {
  return (
    <PageHeader
      back={
        <Link className="page__back" to="/history">
          ← History
        </Link>
      }
      /* Repeats the `<h1>` verbatim, so it is decoration (R25) -- but it is
         rendered rather than dropped, because it holds the `<h1>` at the same
         height the loaded header will put it at, and nothing here may be
         derived from the id. */
      eyebrow="Recording"
      eyebrowHidden
      title="Recording"
    />
  )
}
